import { Autocomplete, Select, MenuItem } from '@getgo/chameleon-material-ui';
import React, { useEffect } from 'react';
import { defineMessages, FormattedMessage } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import {
  isWhoObjectsLoadingSelector,
  selectTaskRelatedThemeItems,
  whoObjectNamesSelector,
  whoObjectsSelector,
} from '../salesforce.selectors';
import { injectedIntlSelector } from '../../settings/settings.selector';
import { Box } from '@material-ui/core';
import { clearWhoObjects } from '../salesforce.actions';
import { getActionFacade } from '../../actionFacade/action.facade.store';
import { SalesforceActionFacadeBase } from '../salesforce.actionfacade-base';
import { Typography } from '../../theme/Typography.component';
import { makeStyles } from '@material-ui/core/styles';

const definedMessages = defineMessages({
  PLACEHOLDER: {
    id: 'Salesforce.ObjectPicker.Placeholder',
    defaultMessage: 'Search {objectType}',
  },
  LABEL: {
    id: 'Salesforce.WhoPicker.Label',
    defaultMessage: 'Name',
  },
  NO_OPTIONS_FOUND: {
    id: 'Salesforce.ObjectPicker.Empty',
    defaultMessage: 'No {objectType} found',
  },
  LOADING: {
    id: 'Salesforce.ObjectPicker.Loading',
    defaultMessage: 'Loading...',
  },
});

export interface WhoPickerProp {
  onChange: (selectedWho?: { id: string; name: string }) => void;
  onTypeChange: (selectedType: string) => void;
  selectedWho?: { id: string; name: string };
  selectedWhoType: string;
}

const useStyles = makeStyles(() => ({
  textFieldRoot: {
    '& input': {
      paddingRight: '20px !important',
    },
    '--goto-icon-01': 'var(--goto-icon-04)',
  },
  option: {
    borderWidth: 0,
    '&:hover': {
      background: 'var(--goto-option-background-hover) !important',
      color: 'var(--goto-option-color-selected) !important',
    },
    '&[data-focus="true"]': {
      background: 'var(--goto-option-background-hover) !important',
      color: 'var(--goto-option-color-selected) !important',
    },
    '&[aria-selected="true"]': {
      background: 'var(--goto-option-background-selected) !important',
      color: 'var(--goto-option-color-selected) !important',
    },
  },
}));

export const WhoPicker: React.FC<WhoPickerProp> = ({ onChange, onTypeChange, selectedWho, selectedWhoType }) => {
  const intl = useSelector(injectedIntlSelector);
  const whoObjectNames = useSelector(whoObjectNamesSelector);
  const whoObjects = useSelector(whoObjectsSelector);
  const isLoading = useSelector(isWhoObjectsLoadingSelector);
  const themeItems = useSelector(selectTaskRelatedThemeItems);
  const styles = useStyles();

  const allWhoObjects =
    selectedWho && !whoObjects.some((o) => o.id === selectedWho.id) ? [selectedWho, ...whoObjects] : whoObjects;
  const selectedValue = selectedWho ?? null;
  const dispatch = useDispatch();

  useEffect(() => {
    if (selectedWhoType) {
      void getActionFacade<SalesforceActionFacadeBase>().searchWhoObjects(selectedWhoType);
    }
  }, [selectedWhoType]);

  const WhoTypePicker = () => {
    return (
      <Select
        noMargin
        fullWidth
        name="who-type"
        value={selectedWhoType}
        onChange={(event) => {
          onTypeChange(event.target.value);
          dispatch(clearWhoObjects());
        }}
      >
        {whoObjectNames.map((objectType) => (
          <MenuItem key={objectType} value={objectType}>
            <Box display="inline-flex" alignItems="center">
              <Box
                display="inline-flex"
                alignItems="center"
                my={1}
                mr={1}
                style={{ background: `#${themeItems[objectType].color}`, borderRadius: '2px' }}
              >
                {themeItems[objectType].icon ? (
                  <img width={20} height={20} src={themeItems[objectType].icon} alt={objectType} />
                ) : (
                  <div style={{ height: 20, width: 20 }} />
                )}
              </Box>
              {objectType}
            </Box>
          </MenuItem>
        ))}
      </Select>
    );
  };

  return (
    <Box>
      <Box mb={1}>
        <Typography variant="caption-default-02">
          <FormattedMessage {...definedMessages.LABEL} />
        </Typography>
      </Box>
      <Box mb={1}>
        <WhoTypePicker />
      </Box>
      <Autocomplete
        options={allWhoObjects}
        openOnFocus
        forcePopupIcon={false}
        getOptionSelected={(option, selected) => option.id === selected.id}
        getOptionLabel={(option) => option.name}
        value={selectedValue}
        id="salesforce-who-picker"
        onChange={(_, option) => {
          onChange(option ? { id: option.id, name: option.name } : undefined);
        }}
        onInputChange={(_, value, reason) => {
          if (reason === 'input' && !!value && value.length > 2) {
            void getActionFacade<SalesforceActionFacadeBase>().searchWhoObjects(selectedWhoType, value);
          } else if (reason === 'clear') {
            onChange(undefined);
          }
        }}
        loading={isLoading}
        loadingText={<FormattedMessage {...definedMessages.LOADING} />}
        noOptionsText={
          <FormattedMessage {...definedMessages.NO_OPTIONS_FOUND} values={{ objectType: selectedWhoType }} />
        }
        classes={{ option: styles.option }}
        TextFieldProps={{
          classes: { root: styles.textFieldRoot },
          noMargin: true,
          placeholder: intl.formatMessage(definedMessages.PLACEHOLDER, { objectType: selectedWhoType }),
        }}
      />
    </Box>
  );
};
